/*!
 * EGT — /founder  (an editorial MAGAZINE FEATURE about Justin K Kazmierczak).
 *
 * Not a sales page — a credibility profile written to make a skeptic think "this
 * person is the real thing." Typography-forward, editorial rhythm: a cinematic
 * title, a drop-cap opening, pull-quotes, section dividers, generous spacers,
 * a framed editorial portrait, and magazine-style full-bleed media breaks between
 * sections. Everything true (agents.md §4 — no invented facts/dates, no "draft",
 * honest beta posture). Frosted glass over UNDIMMED brand art; gold scarce;
 * Playfair (display) + Inter (body); rem-only; full site nav + footer.
 *
 * ONE primary contact CTA at the BOTTOM — the REAL verified-inquiry form (the same
 * ids/field-names /src/contact-us.js binds to: #contact-us-form / #contact-us-status
 * / #contact-us-submit; fields email / firstname / lastname / phone / howcanwehelp).
 * Business/press/partner inquiries, SEPARATE from the book newsletter. A small
 * secondary CTA to the book is kept (every page CTAs the book).
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.founder",
  title: "Justin K Kazmierczak — Founder of EGT",
  description: "Meet Justin K Kazmierczak, founder of EGT — a house of studios building The Universe across software, AI, creative, music and the novel Exploit.",
  supportServer: true
});
var S = require("../helpers/egt.sections.js");

/* ── editorial primitives ────────────────────────────────────────────── */

/* A narrow reading column (magazine measure). `lede:true` drop-caps the first
 * paragraph. Each para is a string or an array of inline UA/JSON nodes. */
function prose(opts) {
  var head = [];
  if (opts.kicker) { head.push({ n: "p", c: "egt__eyebrow egt-reveal", i: opts.kicker }); }
  if (opts.title) { head.push({ n: "h2", c: "egt-fdr__h2 egt-reveal", i: opts.title }); }
  var paras = (opts.paras || []).map(function (p, idx) {
    var cls = "egt-fdr__p egt-reveal";
    if (opts.lede && idx === 0) { cls += " egt-fdr__lede"; }
    return { n: "p", c: cls, i: p };
  });
  return { n: "section", c: "egt-section egt-fdr__prose" + (opts.c ? " " + opts.c : ""),
    i: { n: "div", c: "egt-fdr__read", i: head.concat(paras) } };
}

/* A full-width pull-quote break — the editorial breath between beats. */
function pullQuote(text, attr) {
  var inner = [ { n: "span", c: "egt-fdr__pq-mark", "aria-hidden": "true", i: "“" },
    { n: "p", c: "egt-fdr__pq-text", i: text } ];
  if (attr) { inner.push({ n: "footer", c: "egt-fdr__pq-attr", i: attr }); }
  return { n: "section", c: "egt-fdr__pq",
    i: { n: "blockquote", c: "egt__shell egt-fdr__pq-inner egt-reveal", i: inner } };
}

/* A magazine-style full-bleed media break (image or poster-first video loop),
 * with a small frosted caption chip anchored bottom-left. Undimmed media. */
function mediaBreak(opts) {
  return { n: "section", c: "egt-section egt-canvas egt-canvas--end egt-fdr__break",
    i: [
      S.media({ img: opts.img, video: opts.video, poster: opts.poster, alt: "", parallax: 0.1 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-fdr__break-inner",
        i: { n: "figcaption", c: "egt-fdr__break-cap egt-glass egt-reveal",
          i: [ { n: "span", c: "egt-fdr__break-k", i: opts.kicker },
            { n: "span", c: "egt-fdr__break-t", i: opts.caption } ] } }
    ] };
}

/* ── the six studios (the proof spine) — every one a REAL product + real art.
 * Honest beta posture (agents.md §4): limited public beta, agency clients on it
 * now; self-serve reserves a spot. No invented capabilities, no client names. */
var STUDIOS = [
  { nm: "The Universe Platform", href: "/platform", tag: "Software",
    line: "Run a whole business from one place — sites, commerce, CRM and content, under your brand. In limited public beta; agency clients are building on it now." },
  { nm: "Intrepid", href: "/intrepid", tag: "AI",
    line: "An AI partner that learns a business and quietly carries the hard parts while you sleep." },
  { nm: "EGT Forge", href: "/forge", tag: "Creative agency",
    line: "A full creative agency — strategy, film, design, and the launch. We build the brand, then run the campaign." },
  { nm: [ "EGT", { n: "span", c: "egt-fdr__star", i: "✽" }, " Studio" ], href: "/studio", tag: "Film",
    line: "One script becomes your characters, your scenes, your final cut — the same faces from the first shot to the last." },
  { nm: "EGT Entertainment", href: "/entertainment", tag: "Music",
    line: "Original records you can stream right now on Apple Music, Spotify and YouTube Music — with more scored to the story." },
  { nm: "Exploit", href: "/exploit", tag: "The novel",
    line: "Book One of The Universe — a cinematic spiritual thriller, with a free opening you can read today." }
];

function studioRow(s, idx) {
  return { n: "a", c: "egt-fdr__studio egt-reveal egt-reveal--up", href: s.href,
    style: "--egt-i:" + idx,
    i: [
      { n: "span", c: "egt-fdr__studio-tag", i: s.tag },
      { n: "span", c: "egt-fdr__studio-nm", i: s.nm },
      { n: "span", c: "egt-fdr__studio-line", i: s.line },
      { n: "span", c: "egt-fdr__studio-go",
        i: [ "Explore", { n: "span", c: "egt-cta__tick", "aria-hidden": "true", i: " ›" } ] }
    ] };
}

/* ── HERO — a cinematic title over undimmed brand art, with the framed portrait. */
function hero() {
  var editorial = {
    n: "div", c: "egt-fdr__hero-lead",
    i: [
      { n: "p", c: "egt__eyebrow egt-reveal", i: "The Founder · A profile" },
      { n: "h1", c: "egt-fdr__title egt-reveal",
        i: [ "Twenty years, ", { n: "br" }, "one ", { n: "span", c: "egt__gold", i: "story." } ] },
      { n: "p", c: "egt-fdr__dek egt-reveal",
        i: [ "How ", { n: "strong", i: "Justin K Kazmierczak" }, " turned a childhood vision into ", { n: "strong", i: "EGT" }, " — a house of studios, and the novel at the center of it all." ] },
      { n: "div", c: "egt-fdr__hero-actions egt-reveal",
        i: [
          { n: "a", c: "egt-cta egt-cta--primary", href: "#founder-contact",
            i: [ "Get in touch", { n: "span", c: "egt-cta__tick", i: "›" } ] },
          { n: "a", c: "egt-cta egt-cta--ghost", href: "/exploit",
            i: [ "Read the book", { n: "span", c: "egt-cta__tick", i: "›" } ] }
        ] }
    ]
  };
  var portrait = {
    n: "figure", c: "egt-fdr__photo egt-reveal",
    i: [
      { n: "div", c: "egt-fdr__photo-frame",
        i: { n: "img", c: "egt-fdr__photo-img", src: "/imgs/founder/justin.jpg",
          alt: "Justin K Kazmierczak, founder of EGT", loading: "eager", decoding: "async", fetchpriority: "high" } },
      { n: "figcaption", c: "egt-fdr__photo-cap",
        i: [ { n: "span", c: "egt-fdr__photo-nm", i: "Justin K Kazmierczak" },
          { n: "span", c: "egt-fdr__photo-role", i: "Founder · EGT" } ] }
    ]
  };
  return { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-fdr__hero",
    i: [
      S.media({ img: "/imgs/egt-about-canvas.jpg", alt: "", parallax: 0.08, priority: true }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-fdr__hero-inner",
        i: [ editorial, portrait ] }
    ] };
}

/* ── PROOF — the six studios, editorial list. */
function proof() {
  return { n: "section", c: "egt-section egt-fdr__proof",
    i: { n: "div", c: "egt-fdr__read egt-fdr__read--wide",
      i: [
        { n: "p", c: "egt__eyebrow egt-reveal", i: "The proof" },
        { n: "h2", c: "egt-fdr__h2 egt-reveal",
          i: [ "Six studios. ", { n: "span", c: "egt__gold", i: "All real." } ] },
        { n: "p", c: "egt-fdr__p egt-reveal",
          i: "He didn't set out to start six companies. He set out to tell one story — and had to build everything the story needed. Each of these is a working product you can open today, not a line on a roadmap." },
        { n: "div", c: "egt-fdr__studios", i: STUDIOS.map(studioRow) }
      ] } };
}

/* ── SOCIALS — an invitation to connect (link out); asks nothing. */
var SOCIALS = [
  { nm: "@theswoleginger", sub: "Instagram", href: "https://instagram.com/theswoleginger", icon: "bi-instagram", ext: true },
  { nm: "@egtuniverse", sub: "Instagram", href: "https://instagram.com/egtuniverse", icon: "bi-instagram", ext: true },
  { nm: "jumpcutking", sub: "YouTube", href: "https://youtube.com/@jumpcutking", icon: "bi-youtube", ext: true },
  { nm: "Exploit", sub: "The book", href: "/exploit", icon: "bi-book", ext: false }
];

function socials() {
  return { n: "section", c: "egt-section egt-fdr__connect",
    i: { n: "div", c: "egt-fdr__read egt-fdr__read--wide",
      i: [
        { n: "p", c: "egt__eyebrow egt-reveal", i: "Connect" },
        { n: "h2", c: "egt-fdr__h2 egt-reveal",
          i: [ "Come say ", { n: "span", c: "egt__gold", i: "hi." } ] },
        { n: "div", c: "egt-fdr__socials egt-reveal",
          i: SOCIALS.map(function (s) {
            return { n: "a", c: "egt-fdr__social", href: s.href,
              target: s.ext ? "_blank" : undefined, rel: s.ext ? "noopener" : undefined,
              i: [
                { n: "i", c: s.icon + " egt-fdr__social-icon", "aria-hidden": "true" },
                { n: "span", c: "egt-fdr__social-txt",
                  i: [ { n: "span", c: "egt-fdr__social-nm", i: s.nm },
                    { n: "span", c: "egt-fdr__social-sub", i: s.sub } ] }
              ] };
          }) }
      ] } };
}

/* ── CONTACT — the ONE primary CTA. Real verified-inquiry form (business / press /
 * partnership), the exact DOM contract /src/contact-us.js binds to. NOT the book
 * newsletter. */
function contact() {
  var editorial = {
    n: "div", c: "egt-fdr__contact-editorial",
    i: [
      { n: "p", c: "egt__eyebrow egt-reveal", i: "For partners, press & collaborators" },
      { n: "h2", c: "egt-fdr__contact-title egt-reveal",
        i: [ "Let's ", { n: "span", c: "egt__gold", i: "talk." } ] },
      { n: "p", c: "egt-fdr__contact-lead egt-reveal",
        i: "Partnership, press, an agency project, or a straight question — send it here and it comes to me. No phone trees, no forms into the void; I reply within one business day." },
      { n: "ul", c: "egt-fdr__trust egt-reveal",
        i: [
          { n: "li", c: "egt-fdr__trust-item",
            i: [ { n: "i", c: "bi-clock-history egt-fdr__trust-icon", "aria-hidden": "true" }, { n: "span", i: "A reply within one business day." } ] },
          { n: "li", c: "egt-fdr__trust-item",
            i: [ { n: "i", c: "bi-shield-lock egt-fdr__trust-icon", "aria-hidden": "true" }, { n: "span", i: "We verify your email, then it comes straight to us — nothing is shared." } ] }
        ] },
      { n: "p", c: "egt-fdr__contact-aux egt-reveal",
        i: [ "Just want the story? ",
          { n: "a", href: "/exploit/free", c: "egt-fdr__contact-auxlink",
            i: [ "Read the free opening", { n: "span", c: "egt-cta__tick", i: " →" } ] } ] }
    ]
  };

  /* EXACT contract for /src/contact-us.js — do NOT rename ids/field names. */
  var formCard = S.glass({
    width: "wide", tone: "dark", c: "egt-fdr__form-card egt-reveal",
    content: [
      { n: "div", id: "contact-us-status", class: "egt-contact__status d-none", role: "status", "aria-live": "polite", tabindex: "-1", i: "" },
      { n: "div", id: "contact-us-form", class: "egt-form egt-contact__form",
        i: { n: "div",
          i: [
            { n: "div", c: "row", i: { n: "div", c: "col-12", i: {
              n: "ua.input", title: "Your email address", icon: "bi-envelope", name: "email", class: "pt-3",
              required: true, autocomplete: "email", placeholder: "you@example.com", type: "email" } } },
            { n: "div", c: "row", i: [
              { n: "div", c: "col-12 col-lg-6", i: {
                n: "ua.input", title: "First name", name: "firstname", placeholder: "Jordan", class: "pt-3",
                required: true, icon: "bi-file-person", minlength: "2", maxlength: "128", type: "text" } },
              { n: "div", c: "col-12 col-lg-6", i: {
                n: "ua.input", title: "Last name", name: "lastname", placeholder: "Avery", class: "pt-3",
                required: true, icon: "bi-file-person", minlength: "2", maxlength: "128", type: "text" } }
            ] },
            { n: "div", c: "row", i: { n: "div", c: "col-12", i: {
              n: "ua.phone", title: "Phone (optional)", icon: "bi-telephone", name: "phone", class: "pt-3",
              required: false, autocomplete: "tel", placeholder: "+16140000009", international: false } } },
            { n: "div", c: "row", i: { n: "div", c: "col-12", i: {
              n: "ua.doc.simple", title: "How can we help?", name: "howcanwehelp", class: "pt-3", required: true } } }
          ] } },
      { n: "p", c: "egt-contact__consent-note", i: [
        "One more step protects us both: before we send, you'll verify your email and agree to the ",
        { n: "a", href: "/legal/codex", target: "_blank", rel: "noopener", c: "egt-contact__codex-link",
          i: "EGT Codex" },
        " (our Terms & Privacy)." ] },
      { n: "div", class: "pt-4", id: "contact-us-submit-row",
        i: { n: "button", id: "contact-us-submit", type: "button", c: "egt-cta",
          i: [ "Send message", { n: "span", c: "egt-cta__tick", i: "›" } ] } }
    ]
  });

  return { n: "section", c: "egt-section egt-canvas egt-canvas--center egt-fdr__contact", id: "founder-contact",
    i: [
      S.media({ img: "/imgs/egt-about-craft.jpg", alt: "", parallax: 0.08 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-fdr__contact-inner",
        i: [ editorial, formCard ] }
    ] };
}

function founderSchema() {
  var O = S.siteOrigin();
  return S.jsonLd([
    /* ProfilePage (Google's profile-page markup) with mainEntity → the Person.
     * SEO wave 2B-3, 2026-10-09. No sameAs until the founder picks his
     * profiles; never the YouTube misspellings as names. */
    { "@type": "ProfilePage", "@id": O + "/founder#webpage", url: O + "/founder",
      name: "Justin K Kazmierczak — Founder of EGT",
      description: "Meet Justin K Kazmierczak, founder of EGT — a house of studios building The Universe across software, AI, creative, film, music and the novel Exploit.",
      isPartOf: { "@id": O + "/#website" },
      about: { "@id": O + "/founder#person" },
      mainEntity: { "@id": O + "/founder#person" },
      breadcrumb: { "@id": O + "/founder#breadcrumb" } },
    { "@type": "Person", "@id": O + "/founder#person",
      name: "Justin K Kazmierczak", alternateName: [ "Justin Kazmierczak" ],
      description: "Author of the novel Exploit, director of the short film The Journey of the Coin, and founder of EGT.",
      jobTitle: "Founder", url: O + "/founder",
      image: O + "/imgs/founder/justin.jpg",
      worksFor: { "@id": O + "/#organization" } },
    { "@type": "BreadcrumbList", "@id": O + "/founder#breadcrumb",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
        { "@type": "ListItem", position: 2, name: "The Founder" }
      ] }
  ]);
}

exports.render = async function render() {
  var story = [
    founderSchema(),
    hero(),

    /* THE VISION — drop-cap opening. */
    prose({ lede: true, kicker: "The vision",
      paras: [
        [ "At about ten years old, Justin K Kazmierczak was handed a vision — not a business plan, a vision: whole worlds, alive. Stories that carry hope, thrill and adventure on the surface, with a deep emotional truth running underneath. He has been chasing it ever since." ],
        [ "So he spent the next twenty years learning how to build it. He dabbled — badly, and stubbornly — in writing, in code, in production, in music, in film. Not to collect skills, but for one reason: to finally be able to tell this one story the way he saw it in his head." ]
      ] }),

    pullQuote([ "He didn't set out to start six companies. He set out to tell ", { n: "em", i: "one story" }, " — and had to build everything the story needed." ]),

    mediaBreak({ video: "/vis/buildthatstory.mp4", poster: "/imgs/egt-studios-canvas.jpg",
      kicker: "The craft", caption: "Build that story." }),

    /* CLIMBING THE WALL — the platform as the ladder. */
    prose({ kicker: "Climbing the wall",
      paras: [
        [ "Between the vision and the page stood a wall: the sheer amount of craft a story like this demands — the software, the systems, the production, the reach. Most people stop there. He built a ladder instead." ],
        [ "That ladder is ", { n: "strong", i: "Universe App Tools" }, " — the Platform. It's the engine that lets one person run everything a modern studio needs from a single place. And once the ladder existed, the climbing turned into proof." ]
      ] }),

    proof(),

    pullQuote([ "No matter how alone you feel, ", { n: "span", c: "egt__gold", i: "you are not alone." } ]),

    mediaBreak({ img: "/imgs/exploit/magic-the-light.jpg",
      kicker: "The novel", caption: "Exploit — Book One of The Universe." }),

    /* THE BOOK AT THE CENTER — Exploit + faith as ground. */
    prose({ kicker: "The book at the center",
      paras: [
        [ { n: "em", i: "Exploit" }, " is a cinematic spiritual thriller about going through outrageous things and still choosing to live by faith — the “exploit” itself: the thing broken people need in order to actually live. Underneath the thrill, its heart is simple and universal: no matter how alone you feel, you are not alone." ],
        /* inbound link to the free opening (SEO wave 2C-13) */
        [ "The opening is free to read now — ", { n: "a", href: "/exploit/free", c: "egt-fdr__inlink", i: "the Foreword, the Prologue and Chapters 1–7" }, "." ],
        [ "It is human-made and AI-assisted, and he'll tell you exactly that — no pretending, no hiding the seams. The honesty is part of the point." ],
        [ "Faith is the ground here, not a gate. Shaped by Valor Christian College and World Harvest Church, Justin builds from it the way you build on bedrock — quietly, and for everyone. “God's got you all,” he'll say, and he means all of you." ]
      ] }),

    /* THE GUY FROM THE GYM — relatable, real. */
    prose({ kicker: "And also",
      paras: [
        [ "He is not a distant figure behind a logo. He's also ", { n: "strong", i: "@theswoleginger" }, " — the guy from the gym, as likely to talk you through a deadlift as a plot beat. Same person, all of it. That's the whole idea." ],
        /* inbound link to /classicman (SEO wave 2C-13) — the show EGT presents */
        [ "EGT also presents ", { n: "a", href: "/classicman", c: "egt-fdr__inlink", i: "The Classic Man Podcast" }, " — Isaiah Carter and Alex Martinez on healthy, non-toxic masculinity." ]
      ] }),

    socials(),
    contact()
  ];
  return S.page("/reserve", [ { n: "div", c: "egt-fdr", i: story } ]);
};
